import { useEffect, useCallback, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE, authedFetch } from "@/lib/api";
import { cn, riskTone } from "@/lib/util";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ClientBrandingModal } from "@/components/security/ClientBrandingModal";
import { ClientHeaderActions } from "@/components/security/client/ClientHeaderActions";
import { ClientAssetsCard } from "@/components/security/client/ClientAssetsCard";
import { ClientShodanCard } from "@/components/security/client/ClientShodanCard";
import { ClientReassignLogCard } from "@/components/security/client/ClientReassignLogCard";
import { SecurityRatingCard } from "@/components/security/SecurityRatingCard";
import { LicensePanel } from "@/components/security/client/LicensePanel";
import { DeleteClientDialog } from "@/components/security/client/DeleteClientDialog";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [shodan, setShodan] = useState(null);
  const [shodanBusy, setShodanBusy] = useState(false);
  const [deepBusy, setDeepBusy] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/clients/${id}`);
    setC(r.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const scanShodan = async (ip) => {
    setShodanBusy(true);
    try {
      const r = await api.post(`/scans/shodan/${encodeURIComponent(ip)}`);
      setShodan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Escaneo Shodan falló");
    } finally { setShodanBusy(false); }
  };

  const runDeepSweep = async () => {
    if (!window.confirm("El barrido profundo expande cada CIDR registrado (hasta 256 hosts por rango) y consume más cuota de la API de Shodan. ¿Continuar?")) return;
    setDeepBusy(true);
    try {
      await api.post(`/scans/shodan-sync?client_id=${encodeURIComponent(id)}&deep=true`);
      toast.success("Barrido profundo en cola — los hallazgos aparecerán a medida que lleguen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo encolar el barrido profundo");
    } finally { setDeepBusy(false); }
  };

  const previewDigest = async () => {
    setDigestBusy(true);
    try {
      const resp = await authedFetch(`${API_BASE}/reports/digest/preview.pdf?client_id=${encodeURIComponent(id)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = `Weekly-Digest-${c.name.replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Vista previa del resumen generada");
    } catch (e) {
      toast.error("Falló la vista previa del resumen: " + e.message);
    } finally { setDigestBusy(false); }
  };

  const deleteClient = async (confirmName) => {
    try {
      await api.delete(`/clients/${id}`, { params: { confirm_name: confirmName } });
      toast.success(`Cliente "${c.name}" eliminado`);
      navigate("/clients");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falló la eliminación");
    }
  };

  if (!c) return <div className="p-8 font-mono text-sm text-muted-foreground">Cargando cliente...</div>;
  const tone = riskTone(c.risk_score);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail">
      <Link to="/clients" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-3 h-3" /> Volver a clientes
      </Link>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Cliente · criticidad {c.criticality}</div>
          <h1 className="font-display font-bold tracking-tight" style={{ fontSize: "clamp(32px, 4vw, 48px)" }}>{c.name}</h1>
          <div className="font-mono text-xs text-muted-foreground mt-2">
            {c.industry || "—"} · {c.country || "—"} · {c.legal_name || c.name}
          </div>
        </div>
        <ClientHeaderActions
          clientId={id}
          client={c}
          tone={tone}
          digestBusy={digestBusy}
          deepBusy={deepBusy}
          onDelete={() => setDeleteOpen(true)}
          onBranding={() => setBrandingOpen(true)}
          onDigestPreview={previewDigest}
          onDeepSweep={runDeepSweep}
        />
      </header>

      <LicensePanel client={c} onRenewed={load} />
      <DeleteClientDialog open={deleteOpen} onOpenChange={setDeleteOpen}
                          clientName={c.name} onConfirm={deleteClient} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Hallazgos" value={c.total} />
        <Stat label="Críticos" value={c.critical} tone="critical" />
        <Stat label="Altos" value={c.high} tone="high" />
        <Stat label="Activos monitoreados" value={c.subdomains_count + (c.domains?.length || 0)} />
      </div>

      <SecurityRatingCard clientId={id} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ClientAssetsCard
          client={c}
          clientId={id}
          onAssetAdded={load}
          onShodanScan={scanShodan}
          shodanBusy={shodanBusy}
        />
        <ClientShodanCard result={shodan} busy={shodanBusy} />
      </div>

      <ClientReassignLogCard clientId={id} />

      <ClientBrandingModal
        clientId={id}
        clientName={c.name}
        open={brandingOpen}
        onClose={() => setBrandingOpen(false)}
      />
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="border border-border bg-card/60 p-4">
      <div className="overline">{label}</div>
      <div className={cn("font-display text-3xl mt-1",
        tone === "critical" && "text-sev-critical",
        tone === "high" && "text-sev-high",
      )}>{value ?? 0}</div>
    </div>
  );
}
